(ns practitest.frontend.components2.dashboard.charts.render
  (:require ["d3" :as d3]
            [malli.core :as m]
            [practitest.frontend.components2.dashboard.charts.common :as c]
            [practitest.frontend.components2.dashboard.charts.scales :as scales]
            [practitest.frontend.components2.dashboard.charts.marks :as marks]
            [practitest.frontend.components2.dashboard.charts.axis :as axis]
            [practitest.frontend.components2.dashboard.charts.render-helpers :as rh]
            [practitest.frontend.components2.dashboard.charts.tooltip :as tooltip]))

(def defaults
  {:vector-effect "non-scaling-stroke"})

(def cartesian-defaults
  {:width   800
   :height  400
   :padding {:top 5 :right 20 :bottom 50 :left 25}
   :encode  {:x {:axis {:orient :bottom}}
             :y {:axis {:orient :left}}}})

(defn- infer-coord-system [{{:keys [x y theta radius]} :encode}]
  (cond
    (or theta radius) :polar
    (or x y)          :cartesian))

(defn- prepare-marks [{:keys [mark marks] :as spec}]
  (cond-> spec
    (and (not marks) mark)
    (->
      (assoc :marks [(if (keyword? mark) {:type mark} mark)])
      (dissoc :mark))))

(defn- build-cartesian-ctx [spec]
  (-> (c/deep-merge cartesian-defaults spec)
      scales/with-scales))

(defn- build-polar-ctx
  "No x/y scales, only color. Radius follows the plot width,
   kept within :min-radius and :max-radius."
  [{:keys [width height min-radius max-radius] :as spec}]
    (-> spec
        (assoc :height height)
        scales/with-color-scale
        (assoc :outer-radius (-> (* 0.54 (/ width 2))
                                 (min (or max-radius js/Infinity))
                                 (max (or min-radius 0)))
               :center-x     (/ width 2)
               :center-y     (/ height 2))))

(defn setup-svg!
  "Apply svg-level attrs: viewBox sized to outer dims, plus a <style> block
   that applies :vector-effect to all descendant paths."
 [svg-el {:keys [vb-w vb-h vector-effect]}]
  (let [svg (-> (.select d3 svg-el)
                (.attr "viewBox" (str "0 0 " vb-w " " vb-h)))]
    (when vector-effect
      (c/ensure-style! svg (str "path { vector-effect: " vector-effect "; }")))
    svg))

(def ^:private legend-bar-height 12)
(def ^:private legend-label-height 24)

(defn- legend-offset [spec]
  (or (get-in spec [:encode :color :legend :offset]) 20))

(defn- dot-legend?
  "Dot + label legend; options :columns and :max-width."
  [spec]
  (= :dot (get-in spec [:encode :color :legend :symbol])))

(defn- dot-legend-columns [spec]
  (let [n (count (get-in spec [:encode :color :scale :domain]))]
    (min n (get-in spec [:encode :color :legend :columns] n))))

(defn- legend-height [spec]
  (cond
    (not (get-in spec [:encode :color :legend]))
    0

    (dot-legend? spec)
    (let [n (count (get-in spec [:encode :color :scale :domain]))]
      (* legend-label-height
         (js/Math.ceil (/ n (max 1 (dot-legend-columns spec))))))

    :else
    (+ legend-bar-height legend-label-height)))

(defn- y-axis-layout!
  "Split layout: sizes the fixed y-axis svg to the plot's height."
  [{:keys [split-targets y-axis-width padding vb-h] :as ctx}]
  (let [svg (-> (setup-svg! (:y-axis split-targets) (assoc ctx :vb-w y-axis-width))
                (.attr "height" vb-h))]
    (-> (c/ensure-group! svg "chart")
        (c/translate! y-axis-width (:top padding))
        (c/ensure-group! "y-axis"))))

(defn cartesian-layout!
  "Ensure chart layout groups exist on the svg and position them.
   Layout:
     .chart       — translated by (padding.left, padding.top)
       .y-axis    — at (0, 0)
       .marks     — at (0, 0), data marks go here
       .x-axis    — at (0, height), after .marks so its baseline paints over the bars
       .caption   — at (0, height + caption-top-offset), when spec has :caption
       .legend    — at (0, height + encode.color.legend.offset)
   In the split layout .y-axis and .legend live in their own svgs."
  [svg {:keys [padding height encode caption split?] :as ctx}]
  (let [legend?        (and (not split?) (get-in encode [:color :legend]))
        caption-height (if caption 15 0)
        g-chart        (-> (c/ensure-group! svg "chart")
                           (c/translate! (:left padding) (:top padding)))]
    (if split?
      (y-axis-layout! ctx)
      (c/ensure-group! g-chart "y-axis"))
    (c/ensure-group! g-chart "marks")
    (-> (c/ensure-group! g-chart "x-axis")
        (c/translate! 0 height))
    (when caption
      (-> (c/ensure-group! g-chart "caption")
          (c/translate! 0 (+ height (:offset caption)))))
    (when legend?
      (-> (c/ensure-group! g-chart "legend")
          (c/translate! 0 (+ height caption-height (legend-offset ctx)))))
    (when-not split?
      (tooltip/setup-tooltip! g-chart))
    svg))

(defn polar-layout!
  "Ensure chart layout groups exist on the svg and position them for a polar
   (arc-based) chart.
   Layout:
     .chart    — translated by (padding.left, padding.top)
       .marks  — translated to (center-x, center-y); d3.arc() paths are
                 centered on (0,0) by construction, so the marks group's
                 origin doubles as the pie's center.
       .legend — at (0, height + encode.color.legend.offset), when spec has a legend"
  [svg {:keys [padding center-x center-y height encode] :as ctx}]
  (let [g-chart (-> (c/ensure-group! svg "chart")
                    (c/translate! (:left padding) (:top padding)))]
    (-> (c/ensure-group! g-chart "marks")
        (c/translate! center-x center-y))
    (when (get-in encode [:color :legend])
      (-> (c/ensure-group! g-chart "legend")
          (c/translate! 0 (+ height (legend-offset ctx)))))
    (tooltip/setup-tooltip! g-chart)
    svg))

(defn- normalize-padding [p]
  (cond
    (number? p) {:top p :right p :bottom p :left p}
    (map? p)    p))

(defn- split-spec
  "Split layout: the y-axis gets its own svg, as wide as the left padding, so
   the plot itself needs none, and the legend gets its own svg below the plot.
   Sizes both svgs up front, so the plot's scroll container already has its
   final size when it's measured."
  [spec {:keys [y-axis legend]}]
  (let [padding      (merge (:padding cartesian-defaults) (normalize-padding (:padding spec)))
        y-axis-width (:left padding)
        legend-h     (legend-height spec)]
    (.attr (.select d3 y-axis) "width" y-axis-width)
    (.attr (.select d3 legend) "height" legend-h)
    (assoc spec
           :split?       true
           :padding      (assoc padding :left 0)
           :y-axis-width y-axis-width
           :legend-w     (.-width (.getBoundingClientRect legend))
           :legend-h     legend-h)))

(defn- normalize-scales [spec]
  (update spec :encode update-vals
          (fn [{:keys [scale] :as channel}]
            (cond-> channel
              (vector? scale) (assoc :scale {:domain scale})))))

(defn- lift-color-palette [{:keys [color-palette] :as spec}]
  (cond-> spec
    color-palette
    (update-in [:encode :color :scale]
               (fn [s]
                 (-> s
                     (update :domain #(or % (vec (keys color-palette))))
                     (update :range  #(or % (vec (vals color-palette)))))))))

(defn- build-context [spec]
  (let [spec         (-> (prepare-marks spec)
                         normalize-scales
                         lift-color-palette
                         (update :padding normalize-padding))
        coord-system (infer-coord-system spec)
        cartesian?   (= coord-system :cartesian)
        polar?       (= coord-system :polar)]
    (-> (c/deep-merge defaults spec)
        (assoc :coord-system coord-system :cartesian? cartesian? :polar? polar?)
        (cond-> cartesian? build-cartesian-ctx
                polar?     build-polar-ctx))))

(defn- resolve-spec-size
  "Resolve canvas and inner sizes with priority:
   spec value -> DOM fallback."
  [svg-el spec]
  (let [rect           (some-> svg-el .getBoundingClientRect)
        dom-w          (.-width rect)
        dom-h          (.-height rect)
        padding        (normalize-padding (or (:padding spec) (:padding cartesian-defaults)))
        legend?        (some? (get-in spec [:encode :color :legend]))
        polar?         (= :polar (infer-coord-system spec))
        offset         (if (and polar? (not legend?))
                         0
                         (legend-offset spec))
        caption-height (if (:caption spec) 15 0)
        bottom-space   (cond
                         (and (:split? spec) legend?) offset
                         (dot-legend? spec)           (+ (or (:bottom padding) 0) offset (legend-height spec))
                         :else                        (+ (or (:bottom padding) 0) offset))
        vb-w           (or (:width spec) (max dom-w (:min-width spec 0)))
        vb-h           (or (:height spec) dom-h)
        width          (- vb-w (or (:left padding) 0) (or (:right padding) 0))
        height         (- vb-h (or (:top padding) 0) bottom-space caption-height)]
    (cond-> spec
      vb-w   (assoc :vb-w vb-w)
      vb-h   (assoc :vb-h vb-h)
      width  (assoc :width width)
      height (assoc :height height))))

(defn- render-legend!
  [legend-g anchor-y ctx]
  (let [legend-cfg   (get-in ctx [:encode :color :legend])
        domain       (get-in ctx [:encode :color :scale :domain])
        range        (get-in ctx [:encode :color :scale :range])
        inactive-set (set (get-in legend-cfg [:opacity :one-of]))
        labels       (get-in ctx [:encode :color :labels])
        items        (mapv (fn [value]
                             {:value    value
                              :label    (get labels value value)
                              :color    (get range (.indexOf domain value))
                              :inactive (contains? inactive-set value)})
                           domain)
        n            (count items)]
    (when (pos? n)
      (let [inner-width     (:width ctx)
            bar-gap         2
            update-duration 350
            on-click        (:on-legend-click ctx)
            tooltip         (:tooltip ctx)
            max-item-width  60
            item-width      (min (/ inner-width n) max-item-width)
            item-x          (fn [i] (+ (/ (- inner-width (* n item-width)) 2) (* i item-width)))
            bar-width       (- item-width bar-gap)
            lbl-key         (fn [^js d] (.-value d))
            color-fn        (fn [^js d] (aget d "color"))
            class-fn        (fn [^js d] (str "legend-item" (when-not (aget d "inactive") " active")))
            sel             (-> legend-g
                                (.selectAll "g.legend-item")
                                (.data (clj->js items) lbl-key))
            entered         (-> (.enter sel)
                                (.append "g")
                                (.attr "class" class-fn)
                                (.attr "transform" (fn [_ i] (str "translate(" (item-x i) ",0)")))
                                (.each (fn [^js d]
                                         (this-as this
                                           (let [g (.select d3 this)]
                                             (-> g
                                                 (.append "rect")
                                                 (.attr "x" 0)
                                                 (.attr "y" 0)
                                                 (.attr "width" bar-width)
                                                 (.attr "height" legend-bar-height)
                                                 (.attr "fill" color-fn))
                                             (-> g
                                                 (.append "foreignObject")
                                                 (.attr "x" 0)
                                                 (.attr "y" legend-bar-height)
                                                 (.attr "width" bar-width)
                                                 (.attr "height" legend-label-height)
                                                 (.append "xhtml:div")
                                                 (.attr "class" "legend-label")
                                                 (.text (.-label d))))))))]

        ;; EXIT: fade out removed items
        (-> (.exit sel)
            (.transition)
            (.duration update-duration)
            (.style "opacity" 0)
            (.remove))

        ;; UPDATE: sync active class, position, width, text, fill
        (-> sel
            (.attr "class" class-fn)
            (.transition)
            (.duration update-duration)
            (.attr "transform" (fn [_ i] (str "translate(" (item-x i) ",0)"))))
        (-> sel (.select "rect") (.attr "fill" color-fn) (.attr "width" bar-width))
        (-> sel (.select "foreignObject") (.attr "width" bar-width))
        (-> sel (.select ".legend-label") (.text (fn [^js d] (.-label d))))

        ;; Wire click on full merged selection (enter + update)
        (let [all (.merge entered sel)]
          (when on-click
            (.on all "click" (fn [^js _event ^js d]
                               (on-click (.-value d)))))

          ;; Tooltip events
          (when tooltip
            (let [{:keys [show! hide!]} tooltip]
              (-> all
                  (.on "mouseenter"
                       (fn [^js _event ^js d]
                         (let [i (.indexOf domain (.-value d))]
                           (show! {:category       (.-label d)
                                   :placement      :above
                                   :anchor-x       (+ (item-x i) (/ bar-width 2))
                                   :anchor-y       anchor-y
                                   :text-centered? true}))))
                  (.on "mouseleave"
                       (fn [] (hide!)))))))))))

(defn- render-dot-legend!
  "Dot + label per color domain value, in centered columns.
   A cut label shows the full text as a tooltip."
  [legend-g anchor-y {:keys [encode width tooltip] :as ctx}]
  (let [dot-radius                    4
        dot-gap                       4
        dot-label-x                   (+ (* 2 dot-radius) dot-gap)
        item-gap                      8
        {:keys [scale labels legend]} (:color encode)
        legend-width                  (min width (:max-width legend width))
        offset-x                      (/ (- width legend-width) 2)
        cols                          (max 1 (dot-legend-columns ctx))
        item-width                    (/ legend-width cols)
        cy                            (/ legend-label-height 2)
        {:keys [show! hide!]}         tooltip]
    (.html legend-g "")
    (doseq [[i value] (map-indexed vector (:domain scale))
            :let [x     (+ offset-x (* (mod i cols) item-width))
                  y     (* (quot i cols) legend-label-height)
                  label (get labels value value)
                  g     (-> (.append legend-g "g")
                            (.attr "class" "legend-item"))
                  _     (-> (.append g "circle")
                            (.attr "class" "legend-dot")
                            (.attr "cx" dot-radius)
                            (.attr "cy" cy)
                            (.attr "r" dot-radius)
                            (.attr "fill" (get (:range scale) i)))
                  text  (-> (.append g "text")
                            (.attr "class" "legend-label")
                            (.attr "x" dot-label-x)
                            (.attr "y" cy)
                            (.attr "dominant-baseline" "central"))]]
      (let [cut?      (rh/ellipsize-label! (.node text) label (- item-width dot-label-x item-gap))
            content-w (+ dot-label-x (.getComputedTextLength (.node text)))]
        (c/translate! g (+ x (/ (- item-width content-w) 2)) y)
        (when (and cut? tooltip)
          (-> g
              (.on "mouseenter" #(show! {:category       label
                                         :placement      :above
                                         :anchor-x       (+ x (/ item-width 2))
                                         :anchor-y       (+ anchor-y y)
                                         :text-centered? true}))
              (.on "mouseleave" #(hide!))))))))

(defn- render-split-legend!
  "Split layout: renders the legend into its own svg below the scrollable plot."
  [{:keys [split-targets legend-w legend-h padding tooltip] :as ctx}]
  (let [svg      (setup-svg! (:legend split-targets) (assoc ctx :vb-w legend-w :vb-h legend-h))
        g-chart  (c/ensure-group! svg "chart")
        legend-g (c/ensure-group! g-chart "legend")
        tooltip  (when tooltip
                   (tooltip/make-tooltip-api (tooltip/setup-tooltip! g-chart) legend-w))]
    ((if (dot-legend? ctx) render-dot-legend! render-legend!)
     legend-g 0 (assoc ctx
                       :width   (- legend-w (:right padding))
                       :tooltip tooltip))))

(defn- render-caption! [svg {:keys [width caption]}]
  (let [grp   (.select svg "g.chart g.caption")]
    (-> grp
        (.selectAll "text")
        (.data #js [(:text caption)])
        (.join "text")
        (.attr "class" "caption-text")
        (.attr "text-anchor" "middle")
        (.attr "x" (/ width 2))
        (.attr "y" 0)
        (.text (fn [^js d] d)))))

(defn- overlay-tooltip-api
  "Split layout: the plot's scroll container clips everything outside it, so
   the plot's tooltip is drawn in an overlay svg over the whole frame instead.
   Before each show the overlay's .chart group is moved onto the plot's, so
   tooltip coordinates stay in plot terms. Scrolling hides the tooltip."
  [svg {:keys [split-targets padding width]}]
  (let [overlay-el            (:overlay split-targets)
        plot-el               (.node svg)
        g-chart               (c/ensure-group! (.select d3 overlay-el) "chart")
        {:keys [show! hide!]} (tooltip/make-tooltip-api (tooltip/setup-tooltip! g-chart) width)]
    (.on (.select d3 (.-parentNode plot-el)) "scroll.tooltip" hide!)
    {:show! (fn [data]
              (let [overlay-rect (.getBoundingClientRect overlay-el)
                    plot-rect    (.getBoundingClientRect plot-el)]
                (c/translate! g-chart
                              (+ (- (.-left plot-rect) (.-left overlay-rect)) (:left padding))
                              (+ (- (.-top plot-rect) (.-top overlay-rect)) (:top padding)))
                (show! data)))
     :hide! hide!}))

(defn- render-marks! [svg ctx]
  (let [tooltip-spec (get-in ctx [:encode :tooltip])
        ctx          (if (:tooltip ctx)
                       (assoc ctx
                              :tooltip      (if (:split? ctx)
                                              (overlay-tooltip-api svg ctx)
                                              (tooltip/make-tooltip-api
                                               (.node (.select svg "g.chart g.chart-tooltip"))
                                               (:width ctx)))
                              :tooltip-spec tooltip-spec)
                       ctx)]
    (when (:cartesian? ctx)
      (axis/render! svg
                    (if (:split? ctx) (.select d3 (get-in ctx [:split-targets :y-axis])) svg)
                    ctx))
    (doseq [mark (:marks ctx)]
      (marks/render! svg mark ctx))
    (when (:caption ctx)
      (render-caption! svg ctx))
    (when (get-in ctx [:encode :color :legend])
      (if (:split? ctx)
        (render-split-legend! ctx)
        ((if (dot-legend? ctx) render-dot-legend! render-legend!)
         (.select svg "g.chart g.legend")
         (+ (:height ctx) (legend-offset ctx))
         ctx)))))

(defn- apply-min-width!
  "Keeps the svg at least :min-width wide (via the --chart-min-width CSS
   var), so its scroll container scrolls horizontally instead of the bars
   getting thinner."
  [svg-el min-width]
  (-> (.select d3 svg-el)
      (.style "--chart-min-width" (some-> min-width (str "px")))))

(defn render-chart!
  "Renders spec into svg-el. When split-targets ({:y-axis el :legend el}) are
   given the split layout is used: svg-el is the horizontally scrollable plot,
   while the y-axis and legend are drawn into those svgs, so they stay in place."
  ([svg-el spec] (render-chart! svg-el spec nil))
  ([svg-el spec split-targets]
   (apply-min-width! svg-el (:min-width spec))
   (let [spec (resolve-spec-size svg-el (cond-> spec split-targets (split-spec split-targets)))
         ctx  (assoc (build-context spec) :split-targets split-targets)
         svg  (setup-svg! svg-el ctx)]
     (cond
       (:cartesian? ctx) (do (cartesian-layout! svg ctx)
                             (render-marks! svg ctx))
       (:polar? ctx)     (do (polar-layout! svg ctx)
                             (render-marks! svg ctx)))
     ctx)))
